import { LitElement, css, html, nothing } from '@lit'
import { search } from '@nova/paths'
import styles from '@nova/styles'

export class NovaCombobox extends LitElement {
  static properties = {
    options: { type: Array },
    selectedOptions: { type: Array },
    inputValue: { type: String, state: true },
    filterParam: { type: String, attribute: 'id' },
  }

  constructor() {
    super()
    this.options = []
    this.selectedOptions = []
    this.inputValue = ''
  }

  connectedCallback() {
    super.connectedCallback()
    this.filterCount = this.closest('.js-details-dropdown')?.querySelector('.js-filter-count')
  }

  get filteredOptions() {
    if (!this.inputValue) return this.options
    const searchValue = this.inputValue.toLowerCase()
    return this.options.filter((option) =>
      option.label.toLowerCase().includes(searchValue),
    )
  }

  get sortedOptions() {
    const selectedValues = this.selectedOptions.map((o) => o.value)
    const [selectedItems, nonSelectedItems] = this.filteredOptions.reduce(
      ([selected, nonSelected], item) => {
        (selectedValues.includes(item.value) ? selected : nonSelected).push(item)
        return [selected, nonSelected]
      },
      [[], []],
    )
    return [...selectedItems, ...nonSelectedItems]
  }

  updated(changed) {
    if (changed.has('inputValue')) {
      this.updateOptionsDisabledState()
    }

    if (changed.has('options') || changed.has('inputValue')) {
      if (this.filterCount) {
        const activeCount = this.sortedOptions.reduce(
          (count, item) =>
            count + (item.active === true || item.active === 'true' ? 1 : 0),
          0,
        )
        this.filterCount.textContent = activeCount > 0 ? `(${activeCount})` : ''
        this.filterCount.dataset.filterCount = activeCount
      }
    }
  }

  updateOptionsDisabledState() {
    const searchValue = this.inputValue.toLowerCase()
    const newOptions = this.options.map((option) => ({
      ...option,
      disabled: !option.value.toLowerCase().includes(searchValue),
    }))
  
    if (JSON.stringify(newOptions) !== JSON.stringify(this.options)) {
      this.options = newOptions
    }
  }

  handleInput(e) {
    this.inputValue = e.target.value
  }

  handleItemClick(e, option) {
    e.preventDefault()
    e.stopPropagation()
    this.selectOption(option)
  }

  selectOption(option) {
    const index = this.selectedOptions.findIndex((o) => o.value === option.value)
    let newSelected = [...this.selectedOptions]

    if (index === -1) {
      newSelected.push(option)

      const universal = this.options.find((o) => o.value === 'Universal')
      if (universal && !newSelected.some((o) => o.value === 'Universal')) {
        newSelected.push(universal)
      }
    } else {
      newSelected.splice(index, 1)
    }

    this.selectedOptions = newSelected
    this.dispatchCustomEvent('nova:filter-combobox:update', {
      param: this.filterParam,
      values: this.selectedOptions,
    })
  }

  removeOption(option) {
    this.selectedOptions = this.selectedOptions.filter((o) => o.value !== option.value)
    this.dispatchCustomEvent('nova:filter-combobox:update', {
      param: this.filterParam,
      values: this.selectedOptions,
    })
  }

  dispatchCustomEvent(eventName, detail, options = {}) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
        bubbles: true,
        composed: true,
        ...options,
      }),
    )
  }

  render() {
    return html`
      <div slot="trigger" class="filter-trigger">
        <sl-input
          placeholder="Search"
          .value=${this.inputValue}
          @input=${this.handleInput}>
          <span slot="prefix">${search}</span>
        </sl-input>
      </div>

      <sl-menu style="--auto-size-available-height: 200px !important;">
        ${!this.filteredOptions.length && this.inputValue
          ? html`<div class="no-results">No results found for "${this.inputValue}"</div>`
          : nothing}

        ${this.sortedOptions.map(
          (option) => html`
            <sl-menu-item
              type="checkbox"
              value=${option.value}
              @click=${(e) => this.handleItemClick(e, option)}
              ?checked=${option.active === 'true' || option.active === true || this.selectedOptions.some((selected) => selected.value === option.value)}
              aria-label=${option.label}
              ?disabled=${option.disabled}>
              <span slot="prefix" class="menu-item__check">
                <sl-icon name="check" library="system" aria-hidden="true"></sl-icon>
              </span>
              ${option.label}

              <input
                type="hidden"
                name="${this.filterParam}[]"
                value="${option.value}"
                ?checked=${this.selectedOptions.some((s) => s.value === option.value)} />
            </sl-menu-item>
          `,
        )}
      </sl-menu>
    `
  }

  static styles = [
    styles.flex,
    styles.variables,
    css`
      :root {
        --auto-size-available-height: 200px;
        --sl-input-focus-ring-color: transparent;
      }

      :host {
        width: 100%;
        padding-bottom: var(--nova-space-xs);
      }

      :host[is-open] {
        height: 270px;
      }

      .filter-trigger {
        position: relative;
      }

      sl-input::part(base) {
        border-radius: 0;
        border: 1px solid var(--nova-brand-neutral);
        height: 57px;
      }

      sl-input::part(base):hover {
        border-color: var(--nova-brand-ink);
      }

      sl-input::part(base).input--focused {
        box-shadow: none;
      }

      sl-input::part(input) {
        font: var(--nova-p3-font);
        font-weight: 700;
        letter-spacing: 1.4px;
        text-transform: uppercase;
        color: var(--nova-brand-ink);
        height: 100%;
        padding: 0 var(--nova-space-xs);
      }

      sl-input::part(input)::placeholder {
        color: var(--nova-brand-ink);
      }

      sl-input span {
        display: flex;
        align-items: center;
      }

      sl-input .icon-search {
        width: 20px;
        height: 23px;
      }

      sl-menu {
        border-radius: 0;
        height: var(--auto-size-available-height);
        border-color: var(--nova-brand-neutral);
        border-top: none;
      }

      sl-menu-item {
        --sl-spacing-x-small: 0px;
      }

      sl-menu-item::part(base) {
        padding: 0.25rem 15px;
        align-items: center;
      }

      sl-menu-item:focus-visible::part(base) {
        color: var(--sl-color-neutral-700);
        background-color: var(--sl-color-neutral-100);
      }

      sl-menu-item::part(checked-icon) {
        display: none;
      }

      sl-menu-item::part(prefix) {
        align-items: center;
        justify-content: center;
        border: 1px solid var(--nova-color-foreground-secondary);
        padding: 4px;
        margin-right: 10px;
        width: 16px;
        height: 16px;
        border-radius: 2px;
        background-color: var(--nova-brand-reverse);
      }

      .menu-item__check sl-icon {
        display: none;
      }

      sl-menu-item[checked] .menu-item__check sl-icon {
        display: block;
      }

      .no-results {
        padding: 0.25rem 15px;
      }
    `,
  ]
}

customElements.define('nova-combobox', NovaCombobox)
